import { useCallback, useRef, useState } from 'react';

/**
 * Lightweight bot protection for public forms, no third-party service needed:
 * - a honeypot field people never see (bots fill every input),
 * - a minimum time between the form appearing and being sent,
 * - a per-browser cooldown so one tab can't flood the inbox.
 * A captcha (Cloudflare Turnstile) can be layered on later once site keys exist.
 */

const STORE_PREFIX = 'gc-sent-';

function lastSent(key) {
  try {
    return Number(window.localStorage.getItem(STORE_PREFIX + key) || 0);
  } catch {
    return 0;
  }
}

function markSent(key) {
  try {
    window.localStorage.setItem(STORE_PREFIX + key, String(Date.now()));
  } catch {
    // private mode: the honeypot and timer still apply
  }
}

/**
 * `check()` returns { ok: true } or { ok: false, silent, message }.
 * `silent` means a bot tripped the honeypot: pretend it worked and send nothing.
 * Call `sent()` after a successful submit.
 */
export function useAntiSpam(key, { minMs = 3000, cooldownMs = 60000 } = {}) {
  const startedAt = useRef(Date.now());
  const [trap, setTrap] = useState('');

  const check = useCallback(() => {
    if (trap.trim()) return { ok: false, silent: true, message: '' };
    if (Date.now() - startedAt.current < minMs) {
      return { ok: false, silent: false, message: 'That was quick. Give it a second and send again.' };
    }
    const wait = lastSent(key) + cooldownMs - Date.now();
    if (wait > 0) {
      const secs = Math.ceil(wait / 1000);
      return { ok: false, silent: false, message: `You just sent one. Try again in ${secs} second${secs === 1 ? '' : 's'}.` };
    }
    return { ok: true, silent: false, message: '' };
  }, [trap, key, minMs, cooldownMs]);

  const sent = useCallback(() => {
    markSent(key);
    startedAt.current = Date.now();
    setTrap('');
  }, [key]);

  const trapProps = {
    value: trap,
    onChange: (e) => setTrap(e.target.value),
  };

  return { check, sent, trapProps };
}
